/**
 * 视频克隆音频 Services
 */
import { pollUntilCompleted } from "@/utils/pollUntilTaskCompleted";
import {
  DataValidator,
  IsNumber,
  IsString,
  Required,
  transformKeys,
} from "@/utils/type-vaildator";
import api, { apiV2 } from "./api";

const PROCESS_STATUS = {
  '1': '处理中',
  '2': '处理成功',
  '3': 'process failed', // 处理错误
  '4': 'process timeout',// 处理超时
} as const;

/** 克隆音频请求参数 */
export interface CloneAudioFromVideoProps {
  audioName: string;
  videoUrl: string;
  userId?: string;
}

/** 克隆音频响应参数 */
export interface CloneAudioFromVideoResponse {
  created_at: string;
  status: string,
  task_id: string,
  updated_at: string
}
export class CloneAudioFromVideoData {
  @Required("createdAt cannot be empty")
  @IsString("createdAt must be a string")
  createdAt: string;

  @Required("status cannot be empty")
  @IsString("status must be a string")
  status: string;

  @Required("taskId cannot be empty")
  @IsString("taskId must be a string")
  taskId: string;
}

const transformAndValidateCloneData = (
  response: CloneAudioFromVideoResponse
): CloneAudioFromVideoData => {
  const transformedData = transformKeys(response);

  try {
    const mainData = DataValidator.validateOrThrow(
      transformedData,
      CloneAudioFromVideoData
    );
    return mainData;
  } catch (error) {
    console.error("Clone audio data validation failed:", error.message);
    throw error;
  }
};

/** 音频生成状态响应参数 */
export interface PollAudioStatusResponse {
  process_status: number,
  sample_url: string,
  task_id: string,
  input_text: string
}
export class PollAudioStatusData {
  @Required("processStatus cannot be empty")
  @IsNumber("processStatus must be a number")
  processStatus: number;

  @IsString("sampleUrl must be a string")
  sampleUrl: string;

  @Required("taskId cannot be empty")
  @IsString("taskId must be a string")
  taskId: string;
}

const transformAndValidatePollAudioStatus = (
  response: PollAudioStatusResponse
): PollAudioStatusData => {
  const transformedData = transformKeys(response);

  try {
    const mainData = DataValidator.validateOrThrow(
      transformedData,
      PollAudioStatusData
    );
    if (mainData.processStatus === 2 && !mainData.sampleUrl) {
      throw new Error("Audio clone process failed, sample_url is empty");
    }
    return mainData;
  } catch (error) {
    console.error("Poll clone audio status data validation failed:", error.message);
    throw error;
  }
};

/**
 * 克隆音频
 * @param audioName 音频名称
 * @param videoUrl 视频url
 * @returns 
 */
export const cloneAudioFromVideo = async ({ audioName, videoUrl, userId }: CloneAudioFromVideoProps) => {
  // 发起生成请求
  const response = await apiV2.post<CloneAudioFromVideoResponse>('/audio-extraction/tasks', {
    audio_name: audioName,
    video_url: videoUrl,
    user_id: userId
  });

  const data = transformAndValidateCloneData(response.data);

  // 轮询获取生成状态直到完成
  const result = await pollUntilCompleted<PollAudioStatusData>(() => pollAudioStatus(data.taskId));

  return result;

}

/**
 * 音频生成状态
 * @param taskId 
 * @returns 
 */
export const pollAudioStatus = async (taskId: string) => {
  const response = await api.get<PollAudioStatusResponse>(`/audio/processing-status/${taskId}`);

  const data = transformAndValidatePollAudioStatus(response.data);

  // 0-未处理，1-处理中，2-处理成功，3-处理失败，4-处理超时
  if (data.processStatus > 1) {
    if ([3, 4].includes(data.processStatus)) {
      throw new Error(`Audio clone ${PROCESS_STATUS[data.processStatus]}`);
    }
    return data;
  }
  return null;
}

